import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getActiveChatLoadingState } from '../../../store/slices/chatsSlice';
import { getUserMenuState, toggleUserMenu } from '../../../store/slices/globalSlice';
import MenuContent from '../../common/Menu/MenuContent';
import Spinner from '../../common/Spinner';
import CrossIcon from '../../icons/CrossIcon';
import styles from './styles.module.scss';

const UserMenu = () => {
    const dispatch = useDispatch();
    const isShow = useSelector(getUserMenuState);
    const isNewChatLoading = useSelector(getActiveChatLoadingState);
    const timeoutRef = useRef(null);
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // HORO-10326: force the drawer closed whenever the route changes.
    // Without this the drawer can persist across navigations (e.g. user
    // opens it, browser-backs to a previous page) and the user lands on
    // a new page with the menu still covering it.
    useEffect(() => {
        dispatch(toggleUserMenu(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Auto-close the mobile drawer when the viewport widens past the
    // tablet breakpoint — the desktop sidebar takes over at that width,
    // so leaving the drawer open layers it on top of the same content
    // (visible in the screenshot: drawer + sidebar both render side by
    // side after a resize). 768px mirrors `$tablet-breakpoint` in the
    // Menu's SCSS; hardcoding here keeps the JS self-contained without
    // adding a JS/CSS shared constants module just for one number.
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) {
                dispatch(toggleUserMenu(false));
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [dispatch]);

    // `localShow` drives the slide animation (motion.div x position).
    // `mounted` keeps the component in the tree long enough for the
    // slide-out animation to play. Previously the component
    // early-returned on `!isShow`, which unmounted the drawer the
    // instant Redux flipped — so external dispatchers (the top-bar
    // hamburger toggling toggleUserMenu(false)) skipped the animation
    // entirely. Now both X-button and hamburger trigger the same
    // animate-then-unmount lifecycle.
    const [localShow, setLocalShow] = useState(isShow);
    const [mounted, setMounted] = useState(isShow);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (isShow) {
            setMounted(true);
            // Defer the slide-in by one frame so the initial mount
            // renders with x=-600 and motion can animate to x=0.
            const raf = requestAnimationFrame(() => setLocalShow(true));
            return () => cancelAnimationFrame(raf);
        }

        setLocalShow(false);
        timeoutRef.current = setTimeout(() => setMounted(false), 300);
    }, [isShow]);

    const onClose = () => {
        // Both X tap and burger tap should reach this path. Dispatching
        // toggleUserMenu(false) feeds the useEffect above which animates
        // out before unmount.
        dispatch(toggleUserMenu(false));
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isShow) {
            document.querySelector('html').style.overflowY = 'unset';
            return;
        }
        document.querySelector('html').style.overflowY = 'hidden';
    }, [isShow]);

    useEffect(() => {
        return () => {
            document.querySelector('html').style.overflowY = 'unset';
        };
    }, []);

    if (!mounted) {
        return null;
    }

    return (
        <motion.div className={styles.wrapper} initial={{ x: -600 }} animate={{ x: localShow ? 0 : -600 }} transition={{ duration: 0.2 }}>
            <div className={styles.overly} onClick={onClose} onKeyDown={onClose} role="presentation">
                {isNewChatLoading && (
                    <div className={styles.spinner}>
                        <Spinner width={100} height={100} />
                    </div>
                )}
            </div>
            <div className={classNames(styles.inner)}>
                <div className={styles.top}>
                    <span className={styles.title}>{t('menu.title')}</span>
                    <button className={styles.btn} onClick={onClose}>
                        <CrossIcon />
                    </button>
                </div>
                {/* HORO-10326: the drawer reuses the sidebar's MenuContent so the
                    hamburger drawer and the desktop sidebar share the same items,
                    layout and behaviour. Logout renders inside MenuContent's
                    footer next to the user pill. */}
                <MenuContent onAfterAction={onClose} />
            </div>
        </motion.div>
    );
};
export default UserMenu;
